import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../../data";

const easeOut = [0.22, 1, 0.36, 1];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: easeOut, delay: 0.35 + i * 0.12 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-[600px] h-[88vh] max-h-[880px] w-full overflow-hidden bg-dark"
    >
      {/* Right-side cinematic image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-y-0 right-0 w-full lg:w-[58%]"
      >
        <img
          src={IMAGES.hero}
          alt="Premium travel architecture"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Gradients: strong on left for text legibility */}
      <div
        className="absolute inset-0 lg:hidden"
        style={{ background: "linear-gradient(180deg, rgba(15,16,18,0.55) 0%, rgba(15,16,18,0.82) 100%)" }}
      />
      <div
        className="absolute inset-0 hidden lg:block"
        style={{
          background:
            "linear-gradient(90deg, #0F1012 0%, #0F1012 34%, rgba(15,16,18,0.85) 46%, rgba(15,16,18,0.15) 70%, rgba(15,16,18,0.55) 100%)",
        }}
      />
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-dark pointer-events-none"
      />

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container-bettr w-full">
          <div className="max-w-[720px] pt-16 sm:pt-20">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: easeOut, delay: 0.2 }}
              className="eyebrow mb-5 sm:mb-7"
              data-testid="hero-eyebrow"
            >
              Better Mobility
            </motion.p>

            <h1
              data-testid="hero-heading"
              className="font-serif text-white font-normal"
              style={{
                fontSize: "clamp(42px, 10vw, 112px)",
                lineHeight: 0.94,
                letterSpacing: "-0.035em",
              }}
            >
              <span className="mask-line">
                <motion.span custom={0} variants={lineVariants} initial="hidden" animate="show" className="block">
                  A better way
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span custom={1} variants={lineVariants} initial="hidden" animate="show" className="block">
                  to <span className="text-gold italic">travel.</span>
                </motion.span>
              </span>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easeOut, delay: 0.85 }}
            >
              <span className="block h-px w-14 bg-gold my-6 sm:my-8" />
              <p className="font-sans text-ivory/85 text-base sm:text-lg leading-relaxed max-w-md">
                Better service. Thoughtful comfort.
                <br />
                Safer journeys.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mt-8 sm:mt-10">
                <a href="mailto:info@bettrmobility.com" data-testid="hero-book-btn" className="btn-gold justify-center">
                  BOOK A JOURNEY <ArrowRight size={15} className="arrow" strokeWidth={1.6} />
                </a>
                <a href="#services" data-testid="hero-explore-btn" className="btn-ghost justify-center">
                  EXPLORE OUR SERVICES
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* subtle scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
      >
        <span className="text-[10px] tracking-[0.3em] text-ivory/40 uppercase">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-px h-8 bg-gradient-to-b from-gold/70 to-transparent"
        />
      </motion.div>
    </section>
  );
};
